import { Check } from 'lucide-react'

import { EmptyState } from '@/shared/components/data'
import { useI18n, useT } from '@/shared/i18n'
import { cn } from '@/shared/lib/cn'

import type { PortalTask } from '../types'

// Del archivo, NO del barrel de `common`: ese índice reexporta componentes que
// escriben, y el portal no puede alcanzar nada que escriba. Hay un test de
// contrato que lo verifica recorriendo el grafo de imports — y otro que
// prohíbe siquiera nombrar esos hooks en un archivo del portal, así que este
// comentario tampoco los nombra.
import { DueDateBadge } from '@/shared/components/common/DueDateBadge'

import { PhaseBadge } from './PhaseBadge'

export interface PortalTasksListProps {
  tasks: readonly PortalTask[]
}

/**
 * Las tareas que el cliente puede ver.
 *
 * Solo llegan las marcadas visibles, así que la lista no muestra "3 de 47": el
 * denominador sería una pista sobre trabajo que el cliente no ve, y una lista
 * que dice "faltan 44" sin poder mostrarlas no informa, preocupa.
 *
 * Fechas en absoluto y nunca en rojo. La urgencia es una conversación del
 * equipo con su propio calendario; al cliente se le dice para cuándo está
 * estimado, no cuánto nos atrasamos.
 */
export function PortalTasksList({ tasks }: PortalTasksListProps) {
  const t = useT()

  if (tasks.length === 0) {
    return (
      <EmptyState
        title={t('portal.tasks.emptyTitle')}
        description={t('portal.tasks.emptyDescription')}
      />
    )
  }

  const pending = tasks.filter((task) => !task.is_completed)
  const done = tasks.filter((task) => task.is_completed)

  return (
    <div className="flex flex-col gap-4">
      <Group title={t('portal.tasks.inProgress')} tasks={pending} />
      <Group title={t('portal.tasks.done')} tasks={done} muted />
    </div>
  )
}

function Group({
  title,
  tasks,
  muted = false,
}: {
  title: string
  tasks: readonly PortalTask[]
  muted?: boolean
}) {
  const { t, formatDate } = useI18n()

  if (tasks.length === 0) return null

  return (
    <section className="flex flex-col gap-1.5">
      <h3 className="flex items-center gap-2 text-2xs tracking-wide text-text-3 uppercase">
        {title}
        <span className="num">{tasks.length}</span>
      </h3>

      <ul
        aria-label={title}
        className="hairline flex flex-col overflow-hidden rounded-[var(--radius-card)] bg-surface-1"
      >
        {tasks.map((task) => (
          <li
            key={task.id}
            className="hairline-b flex flex-wrap items-center gap-3 px-3 py-2.5 last:border-b-0"
          >
            {muted ? (
              <Check
                size={14}
                aria-hidden="true"
                className="shrink-0"
                style={{ color: 'hsl(var(--done))' }}
              />
            ) : null}

            {/* El título de la tarea lo escribió el equipo: viaja tal cual. */}
            <span className={cn('min-w-0 flex-1 text-sm', muted ? 'text-text-3' : 'text-text-1')}>
              {task.title}
            </span>

            {muted ? null : <PhaseBadge label={task.stage_label} tone={task.stage_tone} />}

            {/*
              La fecha con su estado, no pelada.

              Antes se mostraba `17/09/2026` y nada más: una tarea vencida y una
              que vence el mes que viene se veían igual, y una que espera al
              cliente no le decía que la pelota era suya (QA funcional 4.3).
              `DueDateBadge` comunica el estado con color + ícono + texto, que
              es la misma regla del lado interno — y acá vale más, porque del
              otro lado no hay nadie a quien preguntarle.

              Las completadas no llevan badge: su fecha ya no significa nada.
            */}
            {task.is_completed ? (
              <span className="num shrink-0 text-2xs text-text-3">
                {task.due_date ? formatDate(task.due_date) : t('due.status.none')}
              </span>
            ) : (
              <DueDateBadge date={task.due_date} className="shrink-0" />
            )}
          </li>
        ))}
      </ul>
    </section>
  )
}
